<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import TalkviaForumAPI from 'dashboard/api/talkviaForum';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ForumComposerExtras from './ForumComposerExtras.vue';

const router = useRouter();
const { accountScopedRoute } = useAccount();
const loading = ref(true);
const saving = ref(false);
const showCreateModal = ref(false);
const topicFiles = ref([]);
const topicMentionedUsers = ref([]);
const overview = ref({ categories: [], membership: {}, stats: {} });
const topics = ref([]);
const meta = ref({ page: 1, total: 0, has_more: false });
const filters = reactive({
  q: '',
  category_id: '',
  sort: 'activity',
  solved: '',
});
const form = reactive({ title: '', body: '', talkvia_forum_category_id: '' });

const canPublishTopic = computed(
  () =>
    form.title.trim().length >= 3 &&
    form.body.trim().length >= 2 &&
    Boolean(form.talkvia_forum_category_id) &&
    !saving.value
);

const categoryOptions = computed(() => [
  { value: '', label: 'Todas as categorias' },
  ...overview.value.categories.map(category => ({
    value: category.id,
    label: category.name,
  })),
]);
const sortOptions = [
  { value: 'activity', label: 'Atividade recente' },
  { value: 'newest', label: 'Mais novos' },
  { value: 'popular', label: 'Mais visualizados' },
];
const solvedOptions = [
  { value: '', label: 'Todos os tópicos' },
  { value: 'true', label: 'Resolvidos' },
  { value: 'false', label: 'Sem solução' },
];

const loadTopics = async (page = 1, append = false) => {
  try {
    const params = { ...filters, page };
    Object.keys(params).forEach(
      key => params[key] === '' && delete params[key]
    );
    const { data } = await TalkviaForumAPI.topics(params);
    topics.value = append ? [...topics.value, ...data.payload] : data.payload;
    meta.value = data.meta;
  } catch (error) {
    useAlert('Não foi possível carregar os tópicos da comunidade.');
  }
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TalkviaForumAPI.overview();
    overview.value = data;
    form.talkvia_forum_category_id = data.categories[0]?.id || '';
    await loadTopics();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível abrir a comunidade.'
    );
  } finally {
    loading.value = false;
  }
};

const createTopic = async () => {
  if (!canPublishTopic.value) {
    useAlert('Use ao menos 3 caracteres no título e 2 nos detalhes.');
    return;
  }
  saving.value = true;
  try {
    const { data } = await TalkviaForumAPI.createTopic(
      form,
      topicFiles.value,
      topicMentionedUsers.value.map(user => user.id)
    );
    showCreateModal.value = false;
    router.push(
      accountScopedRoute('talkvia_forum_topic', { topicId: data.id })
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível publicar o tópico.'
    );
  } finally {
    saving.value = false;
  }
};

const openTopic = topic =>
  router.push(accountScopedRoute('talkvia_forum_topic', { topicId: topic.id }));
const formatDate = value =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

onMounted(load);
</script>

<template>
  <main class="flex w-full min-w-0 flex-col gap-5 p-4 sm:p-6">
    <header
      class="forum-hero relative isolate overflow-hidden rounded-2xl p-6 shadow-lg sm:p-8"
    >
      <div class="forum-hero__glow" aria-hidden="true" />
      <div class="forum-hero__world" aria-hidden="true">
        <span class="forum-hero__orbit">
          <span class="forum-hero__satellite" />
        </span>
        <Icon icon="i-lucide-globe-2" class="forum-hero__globe" />
      </div>
      <div
        class="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="max-w-2xl">
          <p
            class="!text-xs !font-semibold uppercase tracking-[0.2em] !text-white/75"
          >
            Comunidade Talkvia
          </p>
          <h1 class="mt-2 !text-2xl !font-semibold !text-white sm:!text-3xl">
            Aprenda, compartilhe e evolua junto
          </h1>
          <p class="mt-2 !text-sm !leading-6 !text-white/85">
            Tire dúvidas, compartilhe boas práticas e encontre soluções
            construídas por quem usa a Talkvia todos os dias.
          </p>
        </div>
        <Button
          v-if="overview.membership.can_participate"
          label="Criar novo tópico"
          icon="i-lucide-plus"
          color="slate"
          class="forum-hero__button flex-shrink-0 !bg-white !text-slate-900 !outline-white/30 hover:!bg-slate-100 focus-visible:!bg-slate-100"
          @click="showCreateModal = true"
        />
      </div>
      <div
        class="relative z-10 mt-6 grid grid-cols-3 gap-3 border-t border-white/20 pt-5"
      >
        <div>
          <p class="!text-xl !font-semibold !text-white">
            {{ overview.stats.topics || 0 }}
          </p>
          <p class="!text-xs !text-white/70">Tópicos</p>
        </div>
        <div>
          <p class="!text-xl !font-semibold !text-white">
            {{ overview.stats.posts || 0 }}
          </p>
          <p class="!text-xs !text-white/70">Respostas</p>
        </div>
        <div>
          <p class="!text-xl !font-semibold !text-white">
            {{ overview.stats.members || 0 }}
          </p>
          <p class="!text-xs !text-white/70">Membros</p>
        </div>
      </div>
    </header>

    <div v-if="loading" class="flex min-h-64 items-center justify-center">
      <Icon icon="i-lucide-loader-2" class="size-7 animate-spin text-n-brand" />
    </div>
    <template v-else>
      <div
        v-if="!overview.membership.can_participate"
        class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4 text-sm text-n-amber-11"
      >
        <strong>Participação suspensa.</strong> Você ainda pode consultar o
        conteúdo, mas não pode publicar ou interagir.
      </div>
      <section class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <button
          v-for="category in overview.categories"
          :key="category.id"
          type="button"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md"
          @click="
            filters.category_id = category.id;
            loadTopics();
          "
        >
          <span
            class="block h-1 w-10 rounded-full"
            :style="{ backgroundColor: category.color }"
          /><strong class="mt-3 block text-sm text-n-slate-12">{{
            category.name
          }}</strong
          ><span class="mt-1 block text-xs leading-5 text-n-slate-10">{{
            category.description
          }}</span
          ><span class="mt-3 block text-xs font-medium text-n-slate-9"
            >{{ category.topics_count }} tópicos</span
          >
        </button>
      </section>

      <section
        class="overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-sm"
      >
        <div
          class="grid gap-3 border-b border-n-weak p-4 lg:grid-cols-[minmax(16rem,1fr)_13rem_12rem_12rem_auto]"
        >
          <Input
            v-model="filters.q"
            type="search"
            placeholder="Buscar dúvidas e soluções"
            @enter="loadTopics()"
          /><Select
            v-model="filters.category_id"
            :options="categoryOptions"
          /><Select v-model="filters.solved" :options="solvedOptions" /><Select
            v-model="filters.sort"
            :options="sortOptions"
          /><Button
            label="Buscar"
            icon="i-lucide-search"
            @click="loadTopics()"
          />
        </div>
        <div
          v-if="!topics.length"
          class="flex flex-col items-center gap-3 px-6 py-16 text-center"
        >
          <Icon
            icon="i-lucide-messages-square"
            class="size-12 text-n-slate-7"
          />
          <p class="text-sm text-n-slate-10">Nenhum tópico encontrado.</p>
        </div>
        <div v-else class="divide-y divide-n-weak">
          <button
            v-for="topic in topics"
            :key="topic.id"
            type="button"
            class="grid w-full gap-4 p-4 text-left transition hover:bg-n-alpha-1 sm:grid-cols-[1fr_auto] sm:p-5"
            @click="openTopic(topic)"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span
                  v-if="topic.pinned"
                  class="rounded-full bg-n-violet-3 px-2 py-0.5 text-xs font-medium text-n-violet-11"
                  >Fixado</span
                ><span
                  v-if="topic.solved"
                  class="rounded-full bg-n-teal-3 px-2 py-0.5 text-xs font-medium text-n-teal-11"
                  >Resolvido</span
                ><span
                  class="rounded-full border border-n-weak px-2 py-0.5 text-xs text-n-slate-10"
                  >{{ topic.category.name }}</span
                >
              </div>
              <h2 class="mt-2 text-base font-semibold text-n-slate-12">
                {{ topic.title }}
              </h2>
              <p class="mt-1 line-clamp-2 text-sm leading-6 text-n-slate-10">
                {{ topic.preview }}
              </p>
              <p class="mt-3 text-xs text-n-slate-9">
                Por {{ topic.author.name }} · atividade
                {{ formatDate(topic.last_activity_at) }}
              </p>
            </div>
            <div
              class="flex items-center gap-5 text-xs text-n-slate-9 sm:flex-col sm:items-end sm:justify-center sm:gap-2"
            >
              <span class="inline-flex items-center gap-1"
                ><Icon icon="i-lucide-message-circle" class="size-4" />{{
                  topic.posts_count
                }}</span
              ><span class="inline-flex items-center gap-1"
                ><Icon icon="i-lucide-eye" class="size-4" />{{
                  topic.views_count
                }}</span
              >
            </div>
          </button>
        </div>
        <div
          v-if="meta.has_more"
          class="border-t border-n-weak p-4 text-center"
        >
          <Button
            label="Carregar mais"
            color="slate"
            variant="faded"
            @click="loadTopics(meta.page + 1, true)"
          />
        </div>
      </section>
    </template>

    <Teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        @click.self="showCreateModal = false"
      >
        <form
          class="flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
          @submit.prevent="createTopic"
        >
          <div
            class="flex items-center justify-between border-b border-n-weak p-5"
          >
            <div>
              <h2 class="text-lg font-semibold text-n-slate-12">
                Criar novo tópico
              </h2>
              <p class="mt-1 text-xs text-n-slate-10">
                Descreva sua dúvida com contexto para receber respostas
                melhores.
              </p>
            </div>
            <Button
              type="button"
              icon="i-lucide-x"
              color="slate"
              variant="ghost"
              @click="showCreateModal = false"
            />
          </div>
          <div class="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
            <Input
              v-model="form.title"
              label="Título"
              placeholder="Resuma sua dúvida em uma frase"
              minlength="3"
              maxlength="160"
              required
            />
            <p class="-mt-3 text-xs text-n-slate-9">
              Mínimo de 3 e máximo de 160 caracteres.
            </p>
            <div>
              <label class="mb-1 block text-sm font-medium text-n-slate-12"
                >Categoria</label
              ><Select
                v-model="form.talkvia_forum_category_id"
                :options="categoryOptions.slice(1)"
                class="w-full [&>select]:w-full"
              />
            </div>
            <TextArea
              v-model="form.body"
              label="Detalhes"
              placeholder="Explique o cenário, o que você já tentou e o resultado esperado."
              min-height="10rem"
              :max-length="20000"
              show-character-count
              required
            />
            <p class="-mt-3 text-xs text-n-slate-9">
              Descreva o contexto usando ao menos 2 caracteres.
            </p>
            <ForumComposerExtras
              v-model:files="topicFiles"
              v-model:mentioned-users="topicMentionedUsers"
              :disabled="saving"
            />
          </div>
          <div class="flex justify-end gap-3 border-t border-n-weak p-4">
            <Button
              type="button"
              label="Cancelar"
              color="slate"
              variant="outline"
              @click="showCreateModal = false"
            /><Button
              type="submit"
              label="Publicar tópico"
              :is-loading="saving"
              :disabled="!canPublishTopic"
            />
          </div>
        </form>
      </div>
    </Teleport>
  </main>
</template>

<style scoped>
.forum-hero {
  background: radial-gradient(
      circle at 78% 10%,
      rgb(255 255 255 / 12%),
      transparent 28%
    ),
    linear-gradient(135deg, #715bd2 0%, #56579d 52%, #31445e 100%);
}

.forum-hero__glow {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(rgb(255 255 255 / 4%) 1px, transparent 1px),
    linear-gradient(90deg, rgb(255 255 255 / 4%) 1px, transparent 1px);
  background-size: 32px 32px;
  mask-image: linear-gradient(to left, black, transparent 68%);
  pointer-events: none;
}

.forum-hero__world {
  position: absolute;
  top: 50%;
  right: clamp(2rem, 13vw, 14rem);
  width: clamp(13rem, 22vw, 21rem);
  aspect-ratio: 1;
  color: white;
  opacity: 0.16;
  pointer-events: none;
  transform: translateY(-50%);
}

.forum-hero__globe {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 0 28px rgb(191 219 254 / 35%));
  animation: forum-globe-drift 12s ease-in-out infinite alternate;
}

.forum-hero__orbit {
  position: absolute;
  z-index: 1;
  inset: -8%;
  border: 1px solid rgb(255 255 255 / 55%);
  border-radius: 9999px;
  transform: rotate(-18deg);
}

.forum-hero__satellite {
  position: absolute;
  top: 47%;
  left: -0.28rem;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 9999px;
  background: white;
  box-shadow: 0 0 16px 4px rgb(191 219 254 / 70%);
  animation: forum-satellite-pulse 2.4s ease-in-out infinite;
}

.forum-hero__button {
  box-shadow: 0 10px 30px rgb(15 23 42 / 22%);
}

@keyframes forum-globe-drift {
  from {
    transform: translate3d(-5px, -3px, 0) rotate(-2deg);
  }
  to {
    transform: translate3d(6px, 4px, 0) rotate(3deg);
  }
}

@keyframes forum-satellite-pulse {
  50% {
    opacity: 0.55;
    transform: scale(0.72);
  }
}

@media (max-width: 1023px) {
  .forum-hero__world {
    right: -3rem;
    opacity: 0.1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .forum-hero__globe,
  .forum-hero__satellite {
    animation: none;
  }
}
</style>
